import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { TelnyxNumberType } from "src/app/enums/telnyx-number-type";
import { ITelnyxNumber } from "src/app/models/telnyx/telnyx-number";
import { ITelnyxNumbersSearchParams } from "src/app/models/telnyx/telnyx-numbers-search-params";
import { TelnyxService } from "src/app/services/telnyx.service";

@Component({
  selector: "app-search-telnyx-number-form",
  templateUrl: "./search-telnyx-number-form.component.html",
  styleUrls: ["./search-telnyx-number-form.component.scss"],
})
export class SearchTelnyxNumberFormComponent implements OnInit, OnDestroy {
  @Input() selectedNumber = "";

  @Output() selectNumber = new EventEmitter<string>();

  @ViewChild("telnyxNumberList", { static: false })
  telnyxNumberListRef: ElementRef;

  numberType = TelnyxNumberType.Local;
  numberSearchString = "";
  isNumberListCollapsed = false;

  readonly numberTypeSwitcherOptions = [
    { label: "LOCAL", value: TelnyxNumberType.Local },
    { label: "TOLL-FREE", value: TelnyxNumberType.TollFree },
  ];

  private _telnyxNumbers: ITelnyxNumber[] = [];
  private _telnyxNumbersLimit = 30;
  private _telnyxNumberListScrollTopLimit = 700;

  //! Telnyx returns an empty numbers array if more than 500 limit requested
  private readonly _searchTelnyxNumbersLimit = 470;
  private readonly _searchStringChange$ = new Subject<string>();

  constructor(private readonly _telnyxService: TelnyxService) {}

  get numberSearchInputPlaceholder() {
    return this.numberType === TelnyxNumberType.Local ? "Type area or city" : "Type toll";
  }

  get showSelectedNumber() {
    return this.selectedNumber.length > 0 || this.showTelnyxNumbers;
  }

  get showTelnyxNumbers() {
    if (this.isNumberListCollapsed) {
      return false;
    }

    const firstMatchingNumber = this.matchingTelnyxNumbers.at(0)?.number ?? "";

    return (
      this.matchingTelnyxNumbers.length > 1 ||
      (firstMatchingNumber.length > 0 && firstMatchingNumber !== this.selectedNumber)
    );
  }

  get matchingTelnyxNumbers() {
    return this.numberSearchString.length > 0 ? this._telnyxNumbers : [];
  }

  changeNumberType(numberType: TelnyxNumberType) {
    this.numberType = numberType;
    this.isNumberListCollapsed = false;
    this.getTelnyxNumbers();
  }

  onSelectNumber(number: string) {
    this.isNumberListCollapsed = true;
    this.selectNumber.emit(number);
  }

  onSearchStringChange(searchString: string) {
    this.numberSearchString = searchString;
    this.isNumberListCollapsed = false;
    this._searchStringChange$.next(searchString);
  }

  getMoreTelnyxNumbersOnScrollEnd() {
    if (this.telnyxNumberListRef.nativeElement.scrollTop >= this._telnyxNumberListScrollTopLimit) {
      this._telnyxNumberListScrollTopLimit += 959;

      //! Telnyx returns an empty numbers array if more than 500 limit requested
      if (this._telnyxNumbersLimit < 470) {
        this._telnyxNumbersLimit += 30;
      }

      this.getTelnyxNumbers();
    }
  }

  ngOnInit() {
    this.getTelnyxNumbers();

    this._searchStringChange$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.getTelnyxNumbers();
    });
  }

  ngOnDestroy() {
    this._searchStringChange$.complete();
  }

  private getTelnyxNumbers() {
    const hasSearchString = this.numberSearchString.length > 0;
    const limit = hasSearchString ? this._searchTelnyxNumbersLimit : this._telnyxNumbersLimit;

    const searchParams: ITelnyxNumbersSearchParams = {
      limit: limit.toString(),
      numberType: this.numberType,
    };

    if (hasSearchString) {
      searchParams.numberPrefix = this.numberSearchString.trim();
    }

    this._telnyxService.getNumbers(searchParams).subscribe(telnyxNumbers => {
      this._telnyxNumbers = telnyxNumbers;
    });
  }
}
